import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { createSubscriptionCheckout, createOneTimeCaseCheckout } from '../../../services/stripeCheckoutService';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

const PLANS = [
  {
    id: 'single_case',
    name: 'Single Case',
    cta: 'Buy One Case',
    price: '$99',
    period: 'one-time',
    credits: 1,
    icon: 'FileText',
    color: 'text-muted-foreground',
    border: 'border-border',
    description: 'Pay per report — no commitment.',
    features: ['1 case credit', 'Full AI report', 'PDF download', 'Expert marketplace access'],
    oneTime: true,
  },
  {
    id: 'starter_monthly',
    name: 'Starter',
    cta: 'Start Starter',
    price: '$250',
    period: '/ month',
    credits: 3,
    icon: 'Zap',
    color: 'text-primary',
    border: 'border-primary/40',
    description: 'Great for occasional investigators.',
    features: ['3 case credits / month', 'All Single Case features', 'Priority AI processing', 'Email support'],
  },
  {
    id: 'professional_monthly',
    name: 'Professional',
    cta: 'Go Professional',
    price: '$500',
    period: '/ month',
    credits: 10,
    icon: 'Award',
    color: 'text-warning',
    border: 'border-warning/50',
    highlight: true,
    description: 'For active adjusters and attorneys.',
    badge: 'Most Popular',
    features: ['10 case credits / month', 'All Starter features', 'Dedicated account manager'],
  },
  {
    id: 'professional_annual',
    name: 'Pro Annual',
    cta: 'Go Pro Annual',
    price: '$399',
    period: '/ month',
    annualNote: 'billed $4,788 / year',
    credits: 10,
    icon: 'TrendingUp',
    color: 'text-success',
    border: 'border-success/40',
    description: 'Same as Professional — billed yearly.',
    badge: 'Save 20%',
    features: ['10 case credits / month', 'All Professional features', 'Annual invoice'],
  },
];

const UpgradePlanModal = ({ isOpen, onClose, userId, userEmail, currentPlan, hasActiveSub }) => {
  const [loading, setLoading] = useState(null); // planId being processed

  if (!isOpen) return null;

  const handleSelect = async (plan) => {
    setLoading(plan.id);
    try {
      // One-time credit purchase — always goes through checkout
      if (plan.oneTime) {
        const result = await createOneTimeCaseCheckout(userId, userEmail);
        if (!result?.success) throw new Error(result?.error || 'Checkout failed');
        if (result?.sessionUrl) window.location.href = result.sessionUrl;
        return;
      }

      // Existing subscriber → in-place upgrade with proration (no new subscription created)
      if (hasActiveSub) {
        const { data, error } = await supabase.functions.invoke('upgrade-subscription', {
          body: { userId, newPlanKey: plan.id },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        if (data?.requiresPayment && data?.paymentUrl) {
          // No saved card — redirect to Stripe-hosted invoice
          window.location.href = data.paymentUrl;
          return;
        }

        const amount = data?.amountCharged != null ? ` $${data.amountCharged.toFixed(2)} charged (prorated).` : '.';
        toast.success(`Switched to ${plan.name}!${amount}`);
        onClose();
        return;
      }

      // New subscriber → full checkout flow
      const result = await createSubscriptionCheckout(plan.id, userId, userEmail);
      if (!result?.success) throw new Error(result?.error || 'Checkout failed');
      if (result?.isTestAccount) {
        toast.success('Test account — no payment required.');
        onClose();
        return;
      }
      if (result?.sessionUrl) window.location.href = result.sessionUrl;
    } catch (err) {
      toast.error(err.message || 'Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-card border border-border rounded-xl shadow-elevation-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Upgrade Your Plan</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Choose the plan that fits your workflow</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1">
        {/* Plan grid */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLANS.map((plan) => {
            const isCurrent = currentPlan === plan.id ||
              (currentPlan === 'monthly' && plan.id === 'starter_monthly') ||
              (currentPlan === 'annual' && plan.id === 'professional_annual');
            const isLoading = loading === plan.id;

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-lg border-2 p-5 transition-smooth ${plan.border} ${
                  plan.highlight ? 'bg-warning/5' : 'bg-card'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${
                    plan.badge === 'Most Popular'
                      ? 'bg-warning text-warning-foreground'
                      : 'bg-success text-success-foreground'
                  }`}>
                    {plan.badge}
                  </span>
                )}

                {/* Icon + name */}
                <div className="flex items-center gap-2 mb-3">
                  <Icon name={plan.icon} size={18} className={plan.color} />
                  <span className={`text-sm font-semibold ${plan.color}`}>{plan.name}</span>
                </div>

                {/* Price */}
                <div className="mb-1">
                  <span className="text-2xl font-bold text-foreground">{plan.price}</span>
                  <span className="text-xs text-muted-foreground ml-1">{plan.period}</span>
                </div>
                {plan.annualNote && (
                  <p className="text-xs text-success font-medium -mt-0.5 mb-1">{plan.annualNote}</p>
                )}

                <p className="text-xs text-muted-foreground mb-4">{plan.description}</p>

                {/* Features */}
                <ul className="space-y-1.5 mb-5 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-1.5 text-xs text-foreground">
                      <Icon name="Check" size={13} className="text-success mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {isCurrent ? (
                  <div className="w-full py-2 text-center text-xs font-medium text-muted-foreground bg-muted rounded-lg">
                    Current plan
                  </div>
                ) : (
                  <Button
                    variant={plan.highlight ? 'default' : 'outline'}
                    size="sm"
                    fullWidth
                    disabled={!!loading}
                    onClick={() => handleSelect(plan)}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Icon name="Loader2" size={14} className="animate-spin" />
                        Redirecting…
                      </span>
                    ) : (
                      plan.cta
                    )}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="px-6 pb-6">
          <p className="text-xs text-muted-foreground text-center">
            All plans are billed securely through Stripe. You can cancel or change your plan at any time via the billing portal.
          </p>
        </div>
        </div>{/* end scrollable body */}
      </div>
    </div>
  );
};

export default UpgradePlanModal;
